import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInstall } from './useInstall';
import { IosInstallSheet } from './IosInstallSheet';

const SNOOZE_KEY = 'install_banner_snoozed_at';
const SNOOZE_MS = 3 * 24 * 60 * 60 * 1000; // re-nudge after 3 days if dismissed

// Prominent, persistent "install the app" banner for the creator PWA. A creator who never
// installs never comes back to a browser tab, so this sits on every screen until installed.
export function InstallAppBanner() {
  const { t } = useTranslation('common');
  const { visible, isInApp, copied, showIos, setShowIos, install, canWebShare, openShareSheet } = useInstall();
  const [snoozed, setSnoozed] = useState(() => {
    try { const at = Number(localStorage.getItem(SNOOZE_KEY) ?? 0); return at > 0 && Date.now() - at < SNOOZE_MS; } catch { return false; }
  });

  if (!visible || snoozed) return null;

  const snooze = () => { try { localStorage.setItem(SNOOZE_KEY, String(Date.now())); } catch { /* ignore */ } setSnoozed(true); };
  const title = isInApp ? t('install.openInBrowserTitle', { defaultValue: 'Откройте в браузере, чтобы установить' }) : t('install.bannerTitle', { defaultValue: 'Установите AdaMar на телефон' });
  const body = isInApp ? t('install.openInBrowserBody', { defaultValue: 'В приложении Instagram установка недоступна. Нажмите «⋯» вверху → «Открыть в браузере», потом «Установить».' }) : t('install.bannerBody', { defaultValue: 'Задания брендов и уведомления — сразу на экране, без браузера.' });
  const cta = isInApp ? (copied ? t('install.copied', { defaultValue: 'Ссылка скопирована' }) : t('install.copyLink', { defaultValue: 'Скопировать ссылку' })) : t('install.bannerCta', { defaultValue: 'Установить' });

  return (
    <>
      <div style={{ margin: '12px 12px 0', padding: 14, borderRadius: 14, border: '1px solid rgba(138,92,255,0.45)', background: 'linear-gradient(135deg, rgba(74,158,255,0.14), rgba(138,92,255,0.14))', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flexShrink: 0, width: 40, height: 40, borderRadius: 10, background: 'rgba(138,92,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#c9b6ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="7" y="2" width="10" height="20" rx="2" /><path d="M11 18h2" />
          </svg>
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{title}</div>
          <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.4, marginTop: 2 }}>{body}</div>
        </div>
        <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'stretch' }}>
          <button type="button" onClick={install}
            style={{ background: 'var(--primary)', border: 'none', color: '#fff', borderRadius: 9, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
            {cta}
          </button>
          <button type="button" onClick={snooze}
            style={{ background: 'transparent', border: 'none', color: 'var(--dim)', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>
            {t('install.later', { defaultValue: 'Позже' })}
          </button>
        </div>
      </div>

      {showIos && <IosInstallSheet onClose={() => setShowIos(false)} canWebShare={canWebShare} openShareSheet={openShareSheet} />}
    </>
  );
}
